import ExcelJS from "exceljs";

class ExportService {
  // Colores del diseño (coinciden con shared-styles.css)
  static COLORS = {
    headerBg: "FF2C3E50",      // #2c3e50 - Azul oscuro para headers
    headerText: "FFFFFFFF",    // Blanco
    sectionBg: "FFF8F9FA",     // #f8f9fa - Gris claro para secciones
    borderColor: "FFDDDDDD",   // #ddd - Bordes
    textPrimary: "FF333333",   // #333 - Texto principal
    textSecondary: "FF666666", // #666 - Texto secundario
    labelColor: "FF2C3E50",    // #2c3e50 - Labels
    grandTotalBg: "FF2C3E50",  // #2c3e50 - Fondo grand total
    evenRowBg: "FFF8F9FA",     // #f8f9fa - Filas pares
  };

  /**
   * Exporta el documento HTML a Excel manteniendo el diseño visual del PDF
   * @param {string} containerId - ID del contenedor HTML
   * @param {string} fileName - Nombre del archivo (sin extensión)
   * @param {Object} options - Opciones adicionales
   */
  static async exportDocumentToExcel(containerId, fileName, options = {}) {
    try {
      const htmlContainer = document.getElementById(containerId);

      if (!htmlContainer) {
        throw new Error(`Contenedor con ID '${containerId}' no encontrado`);
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(options.sheetName || "Documento");

      // Configurar el ancho de las columnas para simular el layout A4
      // 210mm ≈ 8.27 inches, usamos 10 columnas para el layout
      worksheet.columns = [
        { width: 5 },   // A - Margen izquierdo
        { width: 18 },  // B - Columna 1
        { width: 18 },  // C - Columna 2
        { width: 15 },  // D - Columna 3
        { width: 15 },  // E - Columna 4
        { width: 12 },  // F - Columna 5
        { width: 12 },  // G - Columna 6
        { width: 15 },  // H - Columna 7
        { width: 15 },  // I - Columna 8
        { width: 5 },   // J - Margen derecho
      ];

      let currentRow = 2; // Empezar con margen superior

      // Procesar el header del documento
      currentRow = await this.processHeader(htmlContainer, worksheet, currentRow);
      currentRow += 1;

      // Procesar las secciones de información (buyer, conditions, notify, etc.)
      currentRow = this.processInfoSections(htmlContainer, worksheet, currentRow);
      currentRow += 1;

      // Procesar la tabla de productos
      currentRow = this.processProductsTable(htmlContainer, worksheet, currentRow);
      currentRow += 1;

      // Procesar la sección de totales
      currentRow = this.processTotalsSection(htmlContainer, worksheet, currentRow);
      currentRow += 1;

      // Procesar secciones finales (documentation, banking, comments)
      currentRow = this.processFinalSections(htmlContainer, worksheet, currentRow);
      currentRow += 1;

      // Procesar firmas
      currentRow = this.processSignaturesSection(htmlContainer, worksheet, currentRow);

      // Procesar footer/regulaciones
      this.processFooterSection(htmlContainer, worksheet, currentRow);

      // Configurar la página para impresión
      worksheet.pageSetup = {
        paperSize: 9, // A4
        orientation: "portrait",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: {
          left: 0.25,
          right: 0.25,
          top: 0.5,
          bottom: 0.5,
          header: 0.3,
          footer: 0.3,
        },
      };

      // Exportar el archivo
      ExportService._numericTextToNumbers(workbook);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error al exportar documento a Excel:", error);
      throw error;
    }
  }

  /**
   * Procesa el header del documento (logo, nombre empresa, número de documento)
   */
  static async processHeader(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;

    // Buscar información del header
    const header = htmlContainer.querySelector(".header");
    if (!header) return currentRow;

    // Buscar el logo
    const logoImg = header.querySelector(".company-logo img");
    if (logoImg && logoImg.src) {
      try {
        // Intentar agregar el logo
        const response = await fetch(logoImg.src);
        const blob = await response.blob();
        const base64 = await this.blobToBase64(blob);
        
        const imageId = worksheet.workbook.addImage({
          base64: base64.split(",")[1],
          extension: "png",
        });

        worksheet.addImage(imageId, {
          tl: { col: 1, row: currentRow - 1 },
          ext: { width: 120, height: 60 },
        });
        currentRow += 4;
      } catch (e) {
        console.warn("No se pudo agregar el logo:", e);
      }
    }

    // Nombre de la empresa
    const companyName = header.querySelector(".company-name");
    if (companyName) {
      const row = worksheet.getRow(currentRow);
      const cell = row.getCell(2);
      cell.value = companyName.textContent.trim();
      cell.font = { bold: true, size: 16, color: { argb: this.COLORS.headerBg } };
      currentRow++;
    }

    // Dirección de la empresa
    const companyInfo = header.querySelector(".company-info");
    if (companyInfo) {
      const paragraphs = companyInfo.querySelectorAll("p");
      paragraphs.forEach((p) => {
        const text = p.textContent.trim();
        if (text) {
          const row = worksheet.getRow(currentRow);
          const cell = row.getCell(2);
          cell.value = text;
          cell.font = { size: 10, color: { argb: this.COLORS.textSecondary } };
          currentRow++;
        }
      });
    }

    // Información del documento (número, fecha) - lado derecho
    const proformInfo = header.querySelector(".proform-info");
    if (proformInfo) {
      const h5 = proformInfo.querySelector("h5");
      const proformNumber = proformInfo.querySelector(".proform-number");
      const proformDate = proformInfo.querySelector(".proform-date");

      let docRow = startRow;
      if (h5) {
        const row = worksheet.getRow(docRow);
        const cell = row.getCell(8);
        cell.value = h5.textContent.trim();
        cell.font = { bold: true, size: 14, color: { argb: this.COLORS.headerBg } };
        cell.alignment = { horizontal: "right" };
        docRow++;
      }
      if (proformNumber) {
        const row = worksheet.getRow(docRow);
        const cell = row.getCell(8);
        cell.value = proformNumber.textContent.trim();
        cell.font = { bold: true, size: 12, color: { argb: this.COLORS.headerBg } };
        cell.alignment = { horizontal: "right" };
        docRow++;
      }
      if (proformDate) {
        const row = worksheet.getRow(docRow);
        const cell = row.getCell(8);
        cell.value = proformDate.textContent.trim();
        cell.font = { size: 10, color: { argb: this.COLORS.textSecondary } };
        cell.alignment = { horizontal: "right" };
      }
    }

    // Línea separadora debajo del header
    currentRow++;
    for (let col = 2; col <= 9; col++) {
      const cell = worksheet.getRow(currentRow).getCell(col);
      cell.border = {
        bottom: { style: "medium", color: { argb: this.COLORS.headerBg } },
      };
    }
    currentRow++;

    return currentRow;
  }

  /**
   * Procesa las secciones de información (buyer, conditions, etc.)
   */
  static processInfoSections(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;
    const sections = htmlContainer.querySelectorAll(".block-section");

    let leftCol = 2;
    let rightCol = 6;
    let isLeft = true;
    let maxRowUsed = currentRow;

    sections.forEach((section) => {
      const isLeftSection = section.classList.contains("client-section") ||
                           section.classList.contains("shipping-details-section") ||
                           section.classList.contains("documentation-section") ||
                           section.classList.contains("bank-section") ||
                           section.classList.contains("notify-section");

      const startCol = isLeftSection ? leftCol : rightCol;
      let sectionRow = isLeft && isLeftSection ? currentRow : (isLeftSection ? maxRowUsed + 2 : currentRow);

      if (!isLeftSection && isLeft) {
        sectionRow = currentRow;
      }

      // Título de la sección
      const sectionTitle = section.querySelector(".section-title");
      if (sectionTitle) {
        const titleCell = worksheet.getRow(sectionRow).getCell(startCol);
        titleCell.value = sectionTitle.textContent.trim();
        titleCell.font = { bold: true, size: 11, color: { argb: this.COLORS.headerBg } };
        titleCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: this.COLORS.sectionBg },
        };
        // Merge para el título
        worksheet.mergeCells(sectionRow, startCol, sectionRow, startCol + 3);
        sectionRow++;
      }

      // Contenido de la sección (tabla de información)
      const infoTable = section.querySelector(".info-table");
      if (infoTable) {
        const rows = infoTable.querySelectorAll("tr");
        rows.forEach((tr) => {
          const cells = tr.querySelectorAll("td");
          if (cells.length >= 2) {
            const labelCell = worksheet.getRow(sectionRow).getCell(startCol);
            const valueCell = worksheet.getRow(sectionRow).getCell(startCol + 2);

            labelCell.value = cells[0].textContent.trim();
            labelCell.font = { bold: true, size: 9, color: { argb: this.COLORS.labelColor } };
            
            valueCell.value = cells[1].textContent.trim();
            valueCell.font = { size: 9, color: { argb: this.COLORS.textPrimary } };

            // Aplicar fondo y borde
            for (let c = startCol; c <= startCol + 3; c++) {
              const cell = worksheet.getRow(sectionRow).getCell(c);
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: this.COLORS.sectionBg },
              };
              cell.border = {
                bottom: { style: "thin", color: { argb: this.COLORS.borderColor } },
              };
            }

            sectionRow++;
          }
        });
      }

      if (isLeftSection) {
        maxRowUsed = Math.max(maxRowUsed, sectionRow);
        isLeft = false;
      } else {
        isLeft = true;
        currentRow = Math.max(maxRowUsed, sectionRow) + 1;
      }
    });

    return Math.max(currentRow, maxRowUsed) + 1;
  }

  /**
   * Procesa la tabla de productos
   */
  static processProductsTable(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;

    const productsSection = htmlContainer.querySelector(".products-section");
    if (!productsSection) return currentRow;

    // Título de productos
    const sectionTitle = productsSection.querySelector(".section-title");
    if (sectionTitle) {
      const titleCell = worksheet.getRow(currentRow).getCell(2);
      titleCell.value = sectionTitle.textContent.trim();
      titleCell.font = { bold: true, size: 11, color: { argb: this.COLORS.headerBg } };
      currentRow++;
    }

    // Tabla de productos
    const table = productsSection.querySelector(".products-table");
    if (table) {
      const rows = table.querySelectorAll("tr");
      
      rows.forEach((tr, rowIndex) => {
        const cells = tr.querySelectorAll("th, td");
        let colIndex = 2;

        cells.forEach((cell) => {
          const excelCell = worksheet.getRow(currentRow).getCell(colIndex);
          excelCell.value = cell.textContent.trim();

          // Estilos para encabezado (th)
          if (cell.tagName === "TH") {
            excelCell.font = { bold: true, size: 9, color: { argb: this.COLORS.headerText } };
            excelCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: this.COLORS.headerBg },
            };
            excelCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          } else {
            // Estilos para celdas de datos
            excelCell.font = { size: 9, color: { argb: this.COLORS.textPrimary } };
            
            // Filas pares con fondo
            if (rowIndex % 2 === 0) {
              excelCell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: this.COLORS.evenRowBg },
              };
            }
          }

          // Bordes
          excelCell.border = {
            top: { style: "thin", color: { argb: this.COLORS.borderColor } },
            left: { style: "thin", color: { argb: this.COLORS.borderColor } },
            bottom: { style: "thin", color: { argb: this.COLORS.borderColor } },
            right: { style: "thin", color: { argb: this.COLORS.borderColor } },
          };

          colIndex++;
        });

        currentRow++;
      });
    }

    return currentRow;
  }

  /**
   * Procesa la sección de totales
   */
  static processTotalsSection(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;

    const totalsSection = htmlContainer.querySelector(".totals-section");
    if (!totalsSection) return currentRow;

    // Tabla de totales
    const totalsTable = totalsSection.querySelector(".totals-table");
    if (totalsTable) {
      const rows = totalsTable.querySelectorAll("tr");
      
      rows.forEach((tr) => {
        const cells = tr.querySelectorAll("td");
        if (cells.length >= 2) {
          const labelCell = worksheet.getRow(currentRow).getCell(7);
          const amountCell = worksheet.getRow(currentRow).getCell(8);

          labelCell.value = cells[0].textContent.trim();
          amountCell.value = cells[1].textContent.trim();

          // Verificar si es el grand total
          if (tr.classList.contains("grand-total")) {
            labelCell.font = { bold: true, size: 10, color: { argb: this.COLORS.headerText } };
            amountCell.font = { bold: true, size: 10, color: { argb: this.COLORS.headerText } };
            
            labelCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: this.COLORS.grandTotalBg },
            };
            amountCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: this.COLORS.grandTotalBg },
            };
          } else {
            labelCell.font = { bold: true, size: 9, color: { argb: this.COLORS.labelColor } };
            amountCell.font = { size: 9, color: { argb: this.COLORS.textPrimary } };
          }

          labelCell.alignment = { horizontal: "left" };
          amountCell.alignment = { horizontal: "right" };

          // Bordes
          [labelCell, amountCell].forEach((cell) => {
            cell.border = {
              bottom: { style: "thin", color: { argb: this.COLORS.borderColor } },
            };
          });

          currentRow++;
        }
      });
    }

    // Total en palabras
    const totalWords = totalsSection.querySelector("div > strong");
    if (totalWords && totalWords.parentElement) {
      currentRow++;
      const row = worksheet.getRow(currentRow);
      const cell = row.getCell(2);
      cell.value = totalWords.parentElement.textContent.trim();
      cell.font = { size: 9, color: { argb: this.COLORS.textPrimary } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: this.COLORS.sectionBg },
      };
      worksheet.mergeCells(currentRow, 2, currentRow, 9);
      currentRow++;
    }

    return currentRow;
  }

  /**
   * Procesa secciones finales (documentation, banking, comments)
   */
  static processFinalSections(htmlContainer, worksheet, startRow) {
    // Reutilizamos processInfoSections para las secciones finales
    // Las secciones ya están siendo procesadas en processInfoSections
    return startRow;
  }

  /**
   * Procesa la sección de firmas
   */
  static processSignaturesSection(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;

    const signaturesSection = htmlContainer.querySelector(".signatures-section");
    if (!signaturesSection) return currentRow;

    // Título
    const sectionTitle = signaturesSection.querySelector(".section-title");
    if (sectionTitle) {
      const titleCell = worksheet.getRow(currentRow).getCell(2);
      titleCell.value = sectionTitle.textContent.trim();
      titleCell.font = { bold: true, size: 11, color: { argb: this.COLORS.headerBg } };
      currentRow += 2;
    }

    // Cajas de firmas
    const signatureBoxes = signaturesSection.querySelectorAll(".signature-box");
    let col = 3;
    
    signatureBoxes.forEach((box) => {
      const label = box.querySelector(".signature-label");
      if (label) {
        // Espacio para la firma (línea)
        currentRow += 3;
        const lineRow = worksheet.getRow(currentRow);
        for (let c = col; c <= col + 2; c++) {
          lineRow.getCell(c).border = {
            bottom: { style: "thin", color: { argb: this.COLORS.textPrimary } },
          };
        }
        currentRow++;
        
        // Label de la firma
        const labelCell = worksheet.getRow(currentRow).getCell(col);
        labelCell.value = label.textContent.trim();
        labelCell.font = { size: 10, color: { argb: this.COLORS.textSecondary } };
        labelCell.alignment = { horizontal: "center" };
        worksheet.mergeCells(currentRow, col, currentRow, col + 2);
      }
      col += 4;
      currentRow -= 4; // Volver a la misma fila para la segunda firma
    });

    return currentRow + 5;
  }

  /**
   * Procesa el footer/regulaciones
   */
  static processFooterSection(htmlContainer, worksheet, startRow) {
    let currentRow = startRow;

    // Footer
    const footer = htmlContainer.querySelector(".footer");
    if (footer) {
      const text = footer.textContent.trim();
      if (text) {
        const cell = worksheet.getRow(currentRow).getCell(2);
        cell.value = text;
        cell.font = { size: 9, italic: true, color: { argb: this.COLORS.textSecondary } };
        worksheet.mergeCells(currentRow, 2, currentRow, 9);
        currentRow++;
      }
    }

    // Regulaciones
    const regulations = htmlContainer.querySelector(".regulation-section");
    if (regulations) {
      currentRow++;
      const sectionTitle = regulations.querySelector(".section-title");
      if (sectionTitle) {
        const titleCell = worksheet.getRow(currentRow).getCell(2);
        titleCell.value = sectionTitle.textContent.trim();
        titleCell.font = { bold: true, size: 10, color: { argb: this.COLORS.headerBg } };
        currentRow++;
      }

      const content = regulations.querySelector(".regulation-content");
      if (content) {
        const text = content.textContent.trim();
        if (text) {
          const cell = worksheet.getRow(currentRow).getCell(2);
          cell.value = text;
          cell.font = { size: 9, color: { argb: this.COLORS.textSecondary } };
          cell.alignment = { wrapText: true };
          worksheet.mergeCells(currentRow, 2, currentRow + 2, 9);
        }
      }
    }

    return currentRow;
  }

  /**
   * Convierte un Blob a Base64
   */
  static blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Texto con forma de número en el formato de Ajustes ("1.234,56") → número.
   * Las pantallas mandan los montos ya formateados y Excel/Numbers los
   * tomaban como texto: no sumaban ni entraban en fórmulas. El formato de
   * celda repite los decimales y el separador de miles que se veían.
   * Quedan como texto los ceros a la izquierda ("0123") y los de más de 15
   * dígitos (códigos que Excel redondearía).
   * @param {string} text
   * @returns {{ value: number, numFmt: string }|null}
   */
  static _parseFormattedNumber(text) {
    if (typeof text !== "string") return null;
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > 24) return null;

    let format = "dot.comma.2";
    try {
      format = localStorage.getItem("numberFormat") || format;
    } catch (e) {
      // sin localStorage queda el formato por defecto
    }
    const [thousandsSep, decimalSep] = format.split(".");
    const thousands =
      thousandsSep === "dot" ? "." : thousandsSep === "comma" ? "," : "";
    const decimal = decimalSep === "dot" ? "." : ",";
    if (thousands === decimal) return null;

    const esc = (c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const intPattern = thousands
      ? `\\d{1,3}(?:${esc(thousands)}\\d{3})+|\\d+`
      : "\\d+";
    const match = trimmed.match(
      new RegExp(`^(-?)(${intPattern})(?:${esc(decimal)}(\\d+))?$`)
    );
    if (!match) return null;

    const [, sign, intRaw, decRaw = ""] = match;
    const intDigits = thousands ? intRaw.split(thousands).join("") : intRaw;
    if (intDigits.length > 1 && intDigits.startsWith("0")) return null;
    if (intDigits.length + decRaw.length > 15) return null;

    const value = Number(`${sign}${intDigits}${decRaw ? `.${decRaw}` : ""}`);
    if (!Number.isFinite(value)) return null;

    const grouped = thousands && intRaw.includes(thousands);
    const intFmt = grouped ? "#,##0" : "0";
    const numFmt = decRaw ? `${intFmt}.${"0".repeat(decRaw.length)}` : intFmt;
    return { value, numFmt };
  }

  /** Pasa a número las celdas de texto numérico de todas las hojas. */
  static _numericTextToNumbers(workbook) {
    workbook.eachSheet((worksheet) => {
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          const parsed = ExportService._parseFormattedNumber(cell.value);
          if (!parsed) return;
          cell.value = parsed.value;
          cell.numFmt = parsed.numFmt;
        });
      });
    });
  }

  /**
   * Exporta contenido HTML a Excel
   * @param {string} containerId - ID del contenedor HTML
   * @param {string} fileName - Nombre del archivo (sin extensión)
   * @param {Object} options - Opciones adicionales
   */
  static async exportHtmlToExcel(containerId, fileName, options = {}) {
    try {
      const htmlContainer = document.getElementById(containerId);

      if (!htmlContainer) {
        throw new Error(`Contenedor con ID '${containerId}' no encontrado`);
      }

      // Buscar todas las tablas dentro del contenedor
      const tables = htmlContainer.querySelectorAll("table");

      if (tables.length > 0) {
        // Si hay tablas, exportarlas todas
        const workbook = new ExcelJS.Workbook();

        for (let index = 0; index < tables.length; index++) {
          try {
            const table = tables[index];
            // Intentar obtener un nombre descriptivo para la hoja
            let sheetName = this.getSheetName(table, index);

            // Crear worksheet
            const worksheet = workbook.addWorksheet(sheetName);

            // Procesar la tabla y agregar datos con estilos
            this.processTableToWorksheet(table, worksheet);
          } catch (error) {
            console.warn(`Error procesando tabla ${index + 1}:`, error);
            // Si hay error con una tabla, continuar con las siguientes
          }
        }

        // Si se agregaron hojas, exportar el archivo
        if (workbook.worksheets.length > 0) {
          ExportService._numericTextToNumbers(workbook);
          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
          return true;
        }
      } else {
        // Si no hay tablas pero hay contenido HTML, crear una tabla a partir del contenido
        const data = this.extractDataFromHtml(htmlContainer, options);
        if (data.length > 0) {
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet("Datos");

          // Agregar datos
          data.forEach((row, rowIndex) => {
            const excelRow = worksheet.addRow(row);
            // Aplicar estilos a encabezados
            if (rowIndex === 0) {
              excelRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FF4472C4" },
                };
                cell.font.color = { argb: "FFFFFFFF" };
              });
            }
          });

          // Ajustar ancho de columnas
          worksheet.columns.forEach((column) => {
            column.width = 15;
          });

          ExportService._numericTextToNumbers(workbook);
          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      throw error;
    }
  }

  /**
   * Filas de hoja que deben verse como encabezado (azul). Por defecto solo la fila 0.
   * @param {object} [options]
   * @param {Set<number>|number[]} [options.headerRowIndices]
   */
  static _headerRowIndexSet(options) {
    if (!options || options.headerRowIndices == null) return new Set([0]);
    const h = options.headerRowIndices;
    if (h instanceof Set) return h.size ? h : new Set([0]);
    if (Array.isArray(h)) return h.length ? new Set(h) : new Set([0]);
    return new Set([0]);
  }

  /**
   * Mapas de índice de fila → ARGB de fondo (p. ej. última línea de un bloque shipping).
   * @param {object} [options]
   * @param {Record<number, string>|Map<number, string>} [options.rowFillByIndex]
   * @returns {Map<number, string>}
   */
  static _rowFillMap(options) {
    const map = new Map();
    if (!options || options.rowFillByIndex == null) return map;
    const source = options.rowFillByIndex;
    if (source instanceof Map) {
      source.forEach((argb, index) => {
        if (argb) map.set(Number(index), String(argb));
      });
      return map;
    }
    Object.keys(source).forEach((key) => {
      const argb = source[key];
      if (argb) map.set(Number(key), String(argb));
    });
    return map;
  }

  /**
   * @param {object} [options]
   * @param {Array<{ startRow: number, endRow: number, startCol?: number, endCol?: number }>} [options.outlineBorderRanges]
   *   Índices 0-based (misma base que las filas del array `data`).
   * @returns {Array<{ startRow: number, endRow: number, startCol: number, endCol: number }>}
   */
  static _outlineBorderRanges(options) {
    if (!options || !Array.isArray(options.outlineBorderRanges)) return [];
    return options.outlineBorderRanges
      .map((range) => {
        if (!range) return null;
        const startRow = Number(range.startRow);
        const endRow = Number(range.endRow);
        if (!Number.isFinite(startRow) || !Number.isFinite(endRow)) return null;
        const startCol =
          range.startCol == null ? 0 : Math.max(0, Number(range.startCol) || 0);
        const endCol =
          range.endCol == null
            ? null
            : Math.max(startCol, Number(range.endCol) || startCol);
        return {
          startRow: Math.min(startRow, endRow),
          endRow: Math.max(startRow, endRow),
          startCol,
          endCol,
        };
      })
      .filter(Boolean);
  }

  /**
   * @param {object} [options]
   * @param {Record<number, Record<number, object>>|Map} [options.cellStyleByIndex]
   *   rowIndex (0-based) → colIndex (0-based) → partial style `{ font?, numFmt? }`
   * @returns {Map<number, Map<number, object>>}
   */
  static _cellStyleMap(options) {
    const map = new Map();
    if (!options || options.cellStyleByIndex == null) return map;
    const source = options.cellStyleByIndex;
    const assignRow = (rowKey, colMap) => {
      const rowIndex = Number(rowKey);
      if (!Number.isFinite(rowIndex) || !colMap) return;
      const inner = new Map();
      if (colMap instanceof Map) {
        colMap.forEach((style, colKey) => {
          if (style) inner.set(Number(colKey), style);
        });
      } else {
        Object.keys(colMap).forEach((colKey) => {
          const style = colMap[colKey];
          if (style) inner.set(Number(colKey), style);
        });
      }
      if (inner.size) map.set(rowIndex, inner);
    };
    if (source instanceof Map) {
      source.forEach((colMap, rowKey) => assignRow(rowKey, colMap));
    } else {
      Object.keys(source).forEach((rowKey) => assignRow(rowKey, source[rowKey]));
    }
    return map;
  }

  /**
   * Dibuja un borde exterior tipo "caja" alrededor de un rango de celdas.
   * @param {import('exceljs').Worksheet} worksheet
   * @param {Array<{ startRow: number, endRow: number, startCol: number, endCol: number|null }>} ranges
   * @param {number} columnCount
   */
  static _applyOutlineBorders(worksheet, ranges, columnCount) {
    if (!worksheet || !ranges?.length) return;
    const borderColor = { argb: "FF000000" };
    const thin = { style: "thin", color: borderColor };
    const medium = { style: "medium", color: borderColor };

    ranges.forEach((range) => {
      const excelStartRow = range.startRow + 1;
      const excelEndRow = range.endRow + 1;
      const startCol = (range.startCol ?? 0) + 1;
      const endCol =
        range.endCol == null
          ? Math.max(columnCount, startCol)
          : range.endCol + 1;

      for (let r = excelStartRow; r <= excelEndRow; r += 1) {
        const row = worksheet.getRow(r);
        for (let c = startCol; c <= endCol; c += 1) {
          const cell = row.getCell(c);
          const border = {
            top: thin,
            left: thin,
            bottom: thin,
            right: thin,
          };
          if (r === excelStartRow) border.top = medium;
          if (r === excelEndRow) border.bottom = medium;
          if (c === startCol) border.left = medium;
          if (c === endCol) border.right = medium;
          cell.border = border;
        }
      }
    });
  }

  /**
   * @param {import('exceljs').Row} excelRow
   * @param {number} rowIndex
   * @param {Set<number>} headerRows
   * @param {Map<number, string>} rowFills
   * @param {Map<number, Map<number, object>>} [cellStyles]
   */
  static _styleExcelDataRow(
    excelRow,
    rowIndex,
    headerRows,
    rowFills,
    cellStyles = new Map()
  ) {
    if (headerRows.has(rowIndex)) {
      excelRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4472C4" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      return;
    }

    const fillArgb = rowFills.get(rowIndex);
    const rowCellStyles = cellStyles.get(rowIndex);
    excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (fillArgb) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillArgb },
        };
        cell.font = { ...(cell.font || {}), bold: true };
      }
      const style = rowCellStyles?.get(colNumber - 1);
      if (style) {
        if (style.font) {
          cell.font = { ...(cell.font || {}), ...style.font };
        }
        if (style.numFmt) cell.numFmt = style.numFmt;
      }
      const raw = cell.value != null ? String(cell.value) : "";
      if (raw.includes("\n")) {
        cell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: cell.alignment?.horizontal,
        };
      }
    });
  }

  /**
   * Exporta datos estructurados a Excel
   * @param {Array} data - Array de arrays con los datos
   * @param {string} fileName - Nombre del archivo (sin extensión)
   * @param {string} sheetName - Nombre de la hoja
   * @param {object} [options] - p. ej. `{ headerRowIndices, rowFillByIndex, outlineBorderRanges, cellStyleByIndex }`
   */
  static async exportDataToExcel(data, fileName, sheetName = "Datos", options = {}) {
    try {
      if (!data || data.length === 0) {
        throw new Error("No hay datos para exportar");
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);
      const headerRows = ExportService._headerRowIndexSet(options);
      const rowFills = ExportService._rowFillMap(options);
      const cellStyles = ExportService._cellStyleMap(options);
      const outlineRanges = ExportService._outlineBorderRanges(options);

      // Agregar datos
      data.forEach((row, rowIndex) => {
        const excelRow = worksheet.addRow(row);
        ExportService._styleExcelDataRow(
          excelRow,
          rowIndex,
          headerRows,
          rowFills,
          cellStyles
        );
      });

      // Ajustar ancho de columnas
      let columnCount = 0;
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const raw = cell.value != null ? String(cell.value) : "";
          const lines = raw.split("\n");
          const longestLine = lines.reduce(
            (m, line) => Math.max(m, line.length),
            0
          );
          const columnLength = longestLine || 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50);
        columnCount += 1;
      });

      if (!columnCount && data[0]) {
        columnCount = Array.isArray(data[0]) ? data[0].length : 0;
      }
      ExportService._applyOutlineBorders(worksheet, outlineRanges, columnCount);

      ExportService._numericTextToNumbers(workbook);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error al exportar datos a Excel:", error);
      throw error;
    }
  }

  /**
   * Same as exportDataToExcel but returns the blob and filename for email attachment (no download).
   * @param {Array} data - Array of arrays (rows)
   * @param {string} fileName - Base name without extension
   * @param {string} sheetName - Sheet name
   * @param {object} [options] - igual que exportDataToExcel (p. ej. headerRowIndices, rowFillByIndex, outlineBorderRanges)
   * @returns {Promise<{ blob: Blob, fileName: string }>}
   */
  static async exportDataToExcelAsBlob(data, fileName, sheetName = "Datos", options = {}) {
    if (!data || data.length === 0) {
      throw new Error("No hay datos para exportar");
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    const headerRows = ExportService._headerRowIndexSet(options);
    const rowFills = ExportService._rowFillMap(options);
    const cellStyles = ExportService._cellStyleMap(options);
    const outlineRanges = ExportService._outlineBorderRanges(options);
    data.forEach((row, rowIndex) => {
      const excelRow = worksheet.addRow(row);
      ExportService._styleExcelDataRow(
        excelRow,
        rowIndex,
        headerRows,
        rowFills,
        cellStyles
      );
    });
    let columnCount = 0;
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const raw = cell.value != null ? String(cell.value) : "";
        const lines = raw.split("\n");
        const longestLine = lines.reduce(
          (m, line) => Math.max(m, line.length),
          0
        );
        const columnLength = longestLine || 10;
        if (columnLength > maxLength) maxLength = columnLength;
      });
      column.width = Math.min(maxLength + 2, 50);
      columnCount += 1;
    });
    if (!columnCount && data[0]) {
      columnCount = Array.isArray(data[0]) ? data[0].length : 0;
    }
    ExportService._applyOutlineBorders(worksheet, outlineRanges, columnCount);
    ExportService._numericTextToNumbers(workbook);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fullFileName = `${fileName}.xlsx`;
    return { blob, fileName: fullFileName };
  }

  /**
   * Obtiene un nombre descriptivo para la hoja de Excel
   * @param {HTMLElement} table - Elemento tabla
   * @param {number} index - Índice de la tabla
   * @returns {string} Nombre de la hoja
   */
  static getSheetName(table, index) {
    let sheetName = `Datos_${index + 1}`;

    // Buscar si la tabla tiene un título o caption cercano
    const caption = table.querySelector("caption");
    if (caption) {
      sheetName = caption.textContent.trim().substring(0, 31); // Excel limita nombres a 31 caracteres
    } else {
      // Buscar en elementos cercanos que puedan ser títulos
      const prevElement = table.previousElementSibling;
      if (
        prevElement &&
        (prevElement.tagName === "H1" ||
          prevElement.tagName === "H2" ||
          prevElement.tagName === "H3" ||
          prevElement.tagName === "H4" ||
          prevElement.tagName === "H5" ||
          prevElement.tagName === "H6")
      ) {
        sheetName = prevElement.textContent.trim().substring(0, 31);
      }
    }

    // Si el nombre está vacío o es muy corto, usar un nombre por defecto
    if (!sheetName || sheetName.length < 2) {
      sheetName = `Tabla_${index + 1}`;
    }

    // Limpiar el nombre de caracteres no válidos para Excel
    sheetName = sheetName.replace(/[\\/*?:[\]]/g, "_");

    return sheetName;
  }

  /**
   * Extrae datos de un contenedor HTML
   * @param {HTMLElement} container - Contenedor HTML
   * @param {Object} options - Opciones de extracción
   * @returns {Array} Array de arrays con los datos
   */
  static extractDataFromHtml(container, options = {}) {
    const data = [];

    // Buscar todos los campos con clase field-*
    const fieldSpans = container.querySelectorAll('[class*="field-"]');

    if (fieldSpans.length > 0) {
      // Extraer datos de los spans de campos
      const headers = [];
      const values = [];

      fieldSpans.forEach((span) => {
        const className = span.className;
        const fieldKey = className.match(/field-([^\s]+)/)?.[1];

        if (fieldKey) {
          // Si se proporciona un mapeo de campos, usarlo
          const fieldMapping = options.fieldMapping || {};
          const label = fieldMapping[fieldKey] || fieldKey;

          if (!headers.includes(label)) {
            headers.push(label);
            values.push(span.textContent.trim() || "-");
          }
        }
      });

      if (headers.length > 0) {
        data.push(headers);
        data.push(values);
      }
    } else {
      // Si no hay spans de campos, intentar extraer de cualquier estructura
      const textContent = container.textContent.trim();
      if (textContent) {
        // Dividir por líneas y crear una tabla simple
        const lines = textContent.split("\n").filter((line) => line.trim());
        if (lines.length > 0) {
          data.push(["Contenido"]);
          lines.forEach((line) => {
            if (line.trim()) {
              data.push([line.trim()]);
            }
          });
        }
      }
    }

    return data;
  }

  /**
   * Procesa una tabla HTML y la convierte a worksheet de ExcelJS con estilos
   * @param {HTMLElement} table - Elemento tabla HTML
   * @param {Object} worksheet - Worksheet de ExcelJS
   */
  static processTableToWorksheet(table, worksheet) {
    const rows = table.rows;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const tableRow = rows[rowIndex];
      const excelRow = worksheet.addRow();

      for (let colIndex = 0; colIndex < tableRow.cells.length; colIndex++) {
        const tableCell = tableRow.cells[colIndex];
        const cell = excelRow.getCell(colIndex + 1);

        // Obtener el contenido de texto
        const textContent = tableCell.textContent.trim();
        cell.value = textContent;

        // Verificar si tiene contenido en negrita
        const htmlContent = tableCell.innerHTML;
        const hasBoldContent =
          htmlContent.includes("<strong>") || htmlContent.includes("<b>");

        // Aplicar estilos
        if (hasBoldContent) {
          cell.font = { bold: true };
        }

        // Si es encabezado (th), aplicar estilos de encabezado
        if (tableCell.tagName === "TH") {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
      }
    }

    // Ajustar ancho de columnas
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = Math.min(maxLength + 2, 50);
    });
  }

  /**
   * Exporta contenido HTML a Excel en una sola hoja manteniendo el layout
   * @param {string} containerId - ID del contenedor HTML
   * @param {string} fileName - Nombre del archivo (sin extensión)
   * @param {Object} options - Opciones adicionales
   */
  static async exportHtmlToSingleSheet(containerId, fileName, options = {}) {
    try {
      const htmlContainer = document.getElementById(containerId);

      if (!htmlContainer) {
        throw new Error(`Contenedor con ID '${containerId}' no encontrado`);
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(options.sheetName || "Documento");

      let currentRow = 1;

      // Procesar el contenido del HTML en orden
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            const row = worksheet.getRow(currentRow);
            row.getCell(1).value = text;
            currentRow++;
          }
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tagName = node.tagName.toLowerCase();

        // Si es una tabla, procesarla completamente
        if (tagName === "table") {
          this.processTableToWorksheetAtRow(node, worksheet, currentRow);
          currentRow += node.rows.length + 2; // +2 para espacio después de la tabla
          return;
        }

        // Si es un título, agregarlo con estilo
        if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
          const text = node.textContent.trim();
          if (text) {
            const row = worksheet.getRow(currentRow);
            const cell = row.getCell(1);
            cell.value = text;
            cell.font = { bold: true, size: tagName === "h1" ? 16 : tagName === "h2" ? 14 : 12 };
            currentRow += 2;
          }
          return;
        }

        // Si es un div, section, etc., procesar sus hijos
        if (["div", "section", "article", "header", "footer", "main", "aside", "p", "span", "td", "th", "tr", "tbody", "thead", "tfoot"].includes(tagName)) {
          // Para elementos de bloque que contienen texto directo
          if (tagName === "p" || (tagName === "div" && node.children.length === 0)) {
            const text = node.textContent.trim();
            if (text) {
              const row = worksheet.getRow(currentRow);
              row.getCell(1).value = text;
              currentRow++;
            }
            return;
          }

          // Procesar hijos
          for (const child of node.childNodes) {
            processNode(child);
          }
          return;
        }

        // Para otros elementos, procesar sus hijos
        for (const child of node.childNodes) {
          processNode(child);
        }
      };

      // Procesar todo el contenido
      for (const child of htmlContainer.childNodes) {
        processNode(child);
      }

      // Ajustar ancho de columnas
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      // Exportar el archivo
      ExportService._numericTextToNumbers(workbook);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      throw error;
    }
  }

  /**
   * Procesa una tabla HTML y la agrega al worksheet en una fila específica
   * @param {HTMLElement} table - Elemento tabla HTML
   * @param {Object} worksheet - Worksheet de ExcelJS
   * @param {number} startRow - Fila inicial
   */
  static processTableToWorksheetAtRow(table, worksheet, startRow) {
    const rows = table.rows;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const tableRow = rows[rowIndex];
      const excelRow = worksheet.getRow(startRow + rowIndex);

      for (let colIndex = 0; colIndex < tableRow.cells.length; colIndex++) {
        const tableCell = tableRow.cells[colIndex];
        const cell = excelRow.getCell(colIndex + 1);

        // Obtener el contenido de texto
        const textContent = tableCell.textContent.trim();
        cell.value = textContent;

        // Verificar si tiene contenido en negrita
        const htmlContent = tableCell.innerHTML;
        const hasBoldContent =
          htmlContent.includes("<strong>") || htmlContent.includes("<b>");

        // Aplicar estilos
        if (hasBoldContent) {
          cell.font = { bold: true };
        }

        // Si es encabezado (th), aplicar estilos de encabezado
        if (tableCell.tagName === "TH") {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF2C3E50" }, // Color azul oscuro como en el CSS
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }

        // Aplicar bordes
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
      }
    }
  }

  /**
   * Exporta con loading y manejo de errores
   * @param {Function} exportFunction - Función de exportación
   * @param {string} loadingMessage - Mensaje de loading
   * @param {Vue} vueInstance - Instancia de Vue para emitir eventos
   */
  static async exportWithLoading(
    exportFunction,
    loadingMessage = "Generando Excel...",
    vueInstance = null
  ) {
    try {
      if (vueInstance) {
        vueInstance.$root.$emit("show-loading", loadingMessage);
      }

      await exportFunction();

      if (vueInstance) {
        vueInstance.$root.$emit("hide-loading");
      }
    } catch (error) {
      console.error("Error al generar Excel:", error);

      if (vueInstance) {
        vueInstance.$root.$emit("hide-loading");
        vueInstance.$root.$emit("show-error", "Error al generar el Excel");
      }

      throw error;
    }
  }
}

export default ExportService;
